// src/pages/site/PrivacyPolicy.js
// Must stay accurate to what the backend actually stores (backend/src/config/migrate.js)
// and keep the Google API "Limited Use" disclosure, which OAuth verification checks for.
import React from 'react';
import { Link } from 'react-router-dom';
import { LegalPage, SITE } from '../../components/site/SiteLayout';

const UPDATED = '7 October 2026';
const mail = <a href={`mailto:${SITE.email}`}>{SITE.email}</a>;

const sections = [
  {
    id: 'who-we-are', title: 'Who we are',
    body: <>
      <p>
        {SITE.name} ({SITE.url}) is a social media scheduling service operated by <strong>{SITE.company}</strong> ("we", "us", "our").
        We are responsible for the personal information described in this policy.
      </p>
      <p>Questions about this policy or your data can be sent to {mail}.</p>
    </>,
  },
  {
    id: 'information-we-collect', title: 'Information we collect',
    body: <>
      <h3>Information you give us</h3>
      <ul>
        <li><strong>Account details</strong>: your name, email address, password (stored only as a one-way hash), timezone and, if you upload one, a profile picture.</li>
        <li><strong>Content</strong>: the posts, captions, tags, schedules, groups and media files (images and videos) you create or upload.</li>
        <li><strong>Messages</strong>: anything you send us when you contact support.</li>
      </ul>
      <h3>Information from platforms you connect</h3>
      <p>
        When you connect a social media or Google account, you sign in on that platform's own page and choose what to allow.
        We then receive and store:
      </p>
      <ul>
        <li>Access tokens and refresh tokens that let {SITE.name} act on your behalf within the permissions you granted. These are encrypted at rest.</li>
        <li>Basic profile details used to identify the account: account or channel ID, display name, handle, profile picture and, for Google, your email address.</li>
        <li>The pages, organisations or business locations you choose to post to, and their names and IDs.</li>
        <li>The ID and status of each post we publish for you, and any error message returned by the platform.</li>
      </ul>
      <h3>Information collected automatically</h3>
      <ul>
        <li><strong>Session data</strong>: for each signed-in device we record the browser user agent, IP address and sign-in time so you can review and sign out your active sessions.</li>
        <li><strong>Cookies</strong>: we use one essential, HTTP-only cookie to keep you signed in. We do not use advertising or third-party tracking cookies.</li>
        <li><strong>Server logs</strong>: standard request logs (such as IP address, time and requested URL) used for security and troubleshooting.</li>
      </ul>
    </>,
  },
  {
    id: 'google-user-data', title: 'Google user data',
    body: <>
      <div className="legal-note">
        <p>
          <strong>{SITE.name}'s use and transfer to any other app of information received from Google APIs will adhere to
          the <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">Google API Services User Data Policy</a>,
          including the Limited Use requirements.</strong>
        </p>
      </div>
      <p>If you connect a Google service, {SITE.name} requests only the access below and uses it only for the stated purpose:</p>
      <table className="legal-table">
        <thead><tr><th>Google service</th><th>Access requested</th><th>How we use it</th></tr></thead>
        <tbody>
          <tr>
            <td>YouTube</td>
            <td>Manage your YouTube account and upload videos (<code>youtube</code>, <code>youtube.upload</code>)</td>
            <td>To read your channel's name, ID and picture so you can identify it, and to upload the videos you schedule to that channel as public videos, using your post's caption as the video description.</td>
          </tr>
          <tr>
            <td>Google Business Profile</td>
            <td>Manage your business listings (<code>business.manage</code>)</td>
            <td>To list the business accounts and locations you manage so you can choose one, and to publish the local posts (updates) you schedule to that location.</td>
          </tr>
          <tr>
            <td>Google Account</td>
            <td>Basic profile and email address (<code>profile</code>, <code>email</code>)</td>
            <td>To label the connected account in your dashboard with your name, email address and picture.</td>
          </tr>
        </tbody>
      </table>
      <p>In addition:</p>
      <ul>
        <li>We only publish content that you created and scheduled in {SITE.name}. We do not read, edit or delete your other videos, posts or listing information.</li>
        <li>We do not sell Google user data, and we do not use it for advertising, retargeting or interest-based ads.</li>
        <li>We do not use Google user data to develop, improve or train generalised artificial intelligence or machine-learning models.</li>
        <li>We do not transfer Google user data to anyone else except as needed to provide the service you use, to comply with the law, or as part of a merger or acquisition with notice to you.</li>
        <li>Our staff cannot read Google user data unless you give us permission for a specific support request, it is needed for security or to investigate abuse, or the law requires it.</li>
      </ul>
      <p>
        {SITE.name} uses YouTube API Services. By connecting YouTube you also agree to the{' '}
        <a href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer">YouTube Terms of Service</a>, and Google's handling of your data is
        governed by the <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a>.
      </p>
      <p>
        You can revoke {SITE.name}'s access at any time from your{' '}
        <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">Google Account permissions page</a>{' '}
        or by disconnecting the account in {SITE.name}.
      </p>
    </>,
  },
  {
    id: 'other-platforms', title: 'Meta, LinkedIn and X data',
    body: <>
      <p>For other platforms we request only the permissions needed to publish your posts:</p>
      <ul>
        <li><strong>Facebook and Instagram (Meta)</strong>: to list the Facebook Pages and linked Instagram business accounts you manage, read basic Page information, and publish posts to them.</li>
        <li><strong>LinkedIn</strong>: to read your basic profile and email address and publish posts to your profile, or to the company Pages you administer.</li>
        <li><strong>X (Twitter)</strong>: to read your basic profile, publish posts and upload media to your account.</li>
      </ul>
      <p>This data is used under the same rules as Google data above. We do not sell it or use it for advertising.</p>
    </>,
  },
  {
    id: 'how-we-use', title: 'How we use your information',
    body: <>
      <ul>
        <li>To create and run your account, including reviewing and approving new registrations.</li>
        <li>To publish your posts to the accounts you choose, at the times you choose, and show you the results.</li>
        <li>To send service emails, such as email verification, password reset, account approval and security notices. We do not send marketing emails.</li>
        <li>To keep the service secure, prevent abuse and fix problems.</li>
        <li>To meet our legal obligations.</li>
      </ul>
    </>,
  },
  {
    id: 'sharing', title: 'How we share information',
    body: <>
      <p>We do not sell or rent your personal information. We share it only:</p>
      <ul>
        <li><strong>With the platforms you connect</strong>, when we publish your content to them at your request.</li>
        <li><strong>With service providers</strong> who help us run {SITE.name}, such as our hosting provider and email delivery provider. They may process data only on our instructions.</li>
        <li><strong>When required by law</strong>, or to protect the rights, safety and property of our users, the public or us.</li>
        <li><strong>In a business transfer</strong>, such as a merger or sale. You will be told before your information becomes subject to a different privacy policy.</li>
      </ul>
    </>,
  },
  {
    id: 'security', title: 'How we protect your information',
    body: <>
      <ul>
        <li>All traffic to {SITE.name} is encrypted with HTTPS.</li>
        <li>OAuth access and refresh tokens are encrypted at rest with AES-256-GCM.</li>
        <li>Passwords are stored as salted one-way hashes and are never visible to us.</li>
        <li>Access to production systems is restricted to authorised personnel.</li>
      </ul>
      <p>No system is completely secure, but we work to protect your information and will notify you of a breach where the law requires it.</p>
    </>,
  },
  {
    id: 'retention', title: 'How long we keep information',
    body: <>
      <ul>
        <li>We keep your account information and content for as long as your account exists.</li>
        <li>When you disconnect a platform, we delete its stored access and refresh tokens straight away.</li>
        <li>When you delete your account, we delete your profile, posts, media files, groups, connected accounts and their tokens, and sessions immediately.</li>
        <li>A record of administrator actions on an account (such as approval or deletion), including the name and email address involved, is kept for security and audit purposes.</li>
        <li>Server logs and backups are deleted on a rolling basis.</li>
      </ul>
    </>,
  },
  {
    id: 'data-deletion', title: 'Deleting your data',
    body: <>
      <p>You can remove your data at any time:</p>
      <ul>
        <li><strong>Disconnect one platform</strong>: go to <em>Accounts → Manage Accounts</em> in {SITE.name} and remove the account. Its tokens are deleted at once.</li>
        <li><strong>Delete everything</strong>: open your profile (click your name at the bottom of the sidebar), choose <em>Delete my account</em> and confirm with your password.
          All of your data listed in section 8 is permanently deleted.</li>
        <li><strong>Ask us</strong>: email {mail} from the address on your account and we will delete your data within 30 days.</li>
        <li><strong>At the platform</strong>: you can also remove {SITE.name}'s access from the platform's own settings, such as your{' '}
          <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">Google Account permissions</a> or{' '}
          Facebook's <em>Settings → Apps and Websites</em>.</li>
      </ul>
    </>,
  },
  {
    id: 'your-rights', title: 'Your rights',
    body: <>
      <p>
        Depending on where you live, including under the Australian Privacy Act 1988 and the GDPR, you may have the right to access,
        correct, export or delete your personal information, and to object to or restrict how we use it. You can view and update most
        information yourself in {SITE.name}. For anything else, contact {mail}.
      </p>
      <p>
        If you are not satisfied with our response, you may complain to your local privacy regulator, such as the Office of the
        Australian Information Commissioner (<a href="https://www.oaic.gov.au" target="_blank" rel="noopener noreferrer">oaic.gov.au</a>).
      </p>
    </>,
  },
  {
    id: 'international', title: 'International transfers',
    body: <p>Your information may be stored and processed in countries other than your own. Where we transfer it, we take steps to keep it protected as described in this policy.</p>,
  },
  {
    id: 'children', title: 'Children',
    body: <p>{SITE.name} is a business tool and is not intended for anyone under 16. We do not knowingly collect information from children. If you believe a child has given us information, contact us and we will delete it.</p>,
  },
  {
    id: 'changes', title: 'Changes to this policy',
    body: <p>We may update this policy from time to time. The "Last updated" date above shows when it last changed. If we make a significant change, we will tell you by email or in {SITE.name} before it takes effect.</p>,
  },
  {
    id: 'contact', title: 'Contact us',
    body: <>
      <p><strong>{SITE.company}</strong><br />Email: {mail}<br />Website: <a href={SITE.url}>{SITE.url}</a></p>
      <p>See also our <Link to="/terms">Terms of Service</Link>.</p>
    </>,
  },
];

export default function PrivacyPolicy() {
  return (
    <LegalPage
      title="Privacy Policy"
      updated={UPDATED}
      intro={<p>This Privacy Policy explains what information {SITE.name} collects, how we use and protect it, and the choices you have. It applies to our website and the {SITE.name} application.</p>}
      sections={sections}
    />
  );
}
